import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  User, KeyRound, Eye, EyeOff, ArrowRight, AlertTriangle, Sparkles,
  ShieldCheck, Activity, Globe, LineChart, Newspaper, Bot,
} from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../lib/AuthContext";

const DEMO_USER = "admin";
const DEMO_PASS = "admin123";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";

  const fillDemo = () => {
    setUsername(DEMO_USER);
    setPassword(DEMO_PASS);
    setErr("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setErr("Mohon isi username dan password.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await login(username.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login gagal. Periksa kembali kredensial Anda.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-bg-grid" />
      <div className="login-bg-glow" />

      <div className="login-card">
        <div className="login-side">
          <Link to="/" className="login-brand" data-testid="login-back-home">
            <Logo size={44} />
            <div>
              <div className="lb-title">NATIONAL &amp; MONITORING SYSTEM</div>
              <div className="lb-sub">Republik Indonesia</div>
            </div>
          </Link>

          <div className="login-side-eyebrow">
            <Activity size={11} /> SISTEM PEMANTAUAN AKTIF
          </div>

          <h2 className="login-side-h">Sistem Intelijen Pemantauan Nasional</h2>
          <p className="login-side-p">
            Pusat konsolidasi data strategis untuk pengambilan keputusan tingkat negara.
            Menyajikan analitik <strong>risiko wilayah</strong>, <strong>kamtibmas</strong>,
            <strong> harga pangan</strong>, <strong>sentimen media</strong>, serta
            rekomendasi berbasis kecerdasan buatan secara real-time di 38 provinsi
            Republik Indonesia.
          </p>

          <div className="login-side-grid">
            <div className="lss-card">
              <span className="lss-icon"><Globe size={14} /></span>
              <strong>38 Provinsi</strong>
              <small>Heatmap geospasial nasional</small>
            </div>
            <div className="lss-card">
              <span className="lss-icon"><LineChart size={14} /></span>
              <strong>9+ Indikator</strong>
              <small>Makroekonomi &amp; sosial</small>
            </div>
            <div className="lss-card">
              <span className="lss-icon"><Newspaper size={14} /></span>
              <strong>321 Portal</strong>
              <small>Analisis sentimen media</small>
            </div>
            <div className="lss-card">
              <span className="lss-icon"><Bot size={14} /></span>
              <strong>AI Assistant</strong>
              <small>Rekomendasi kontekstual</small>
            </div>
          </div>

          <div className="login-side-foot">
            <ShieldCheck size={12} /> Enkripsi token JWT · Sesi aman 24 jam
          </div>
        </div>

        <div className="login-form-wrap">
          {/* AUTOFILL HELPER BOX — DI ATAS, TERPISAH DARI KOLOM */}
          <section className="login-autofill-box" data-testid="autofill-box">
            <div className="laf-head">
              <Sparkles size={14} />
              <span>KREDENSIAL AKSES</span>
            </div>
            <div className="laf-body">
              <div className="laf-cred">
                <span className="laf-lbl">Username</span>
                <code data-testid="demo-username-display">{DEMO_USER}</code>
              </div>
              <div className="laf-cred">
                <span className="laf-lbl">Password</span>
                <code data-testid="demo-password-display">{DEMO_PASS}</code>
              </div>
            </div>
            <button
              type="button"
              className="laf-fill-btn"
              onClick={fillDemo}
              data-testid="autofill-button"
            >
              ISI OTOMATIS KE FORM <ArrowRight size={14} />
            </button>
          </section>

          {/* LOGIN FORM — KOTAK TERPISAH */}
          <form className="login-form" onSubmit={onSubmit} data-testid="login-form">
            <div className="login-form-head">
              <h3>Login Pengguna</h3>
              <span>Masukkan kredensial Anda</span>
            </div>

            <label className="login-field">
              <span className="lf-lbl">Username</span>
              <div className="lf-inputwrap">
                <User size={15} className="lf-icon" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Masukkan username"
                  autoComplete="username"
                  data-testid="login-username"
                  disabled={busy}
                />
              </div>
            </label>

            <label className="login-field">
              <span className="lf-lbl">Password</span>
              <div className="lf-inputwrap">
                <KeyRound size={15} className="lf-icon" />
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password"
                  disabled={busy}
                />
                <button
                  type="button"
                  className="lf-eye"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Sembunyikan password" : "Tampilkan password"}
                  data-testid="login-toggle-eye"
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </label>

            {err && (
              <div className="login-err" role="alert" data-testid="login-error">
                <AlertTriangle size={14} /> {err}
              </div>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={busy}
              data-testid="login-submit"
            >
              {busy ? "MEMVERIFIKASI…" : "MASUK"}
              {!busy && <ArrowRight size={15} />}
            </button>

            <Link to="/" className="login-back" data-testid="login-back">
              ← Kembali ke halaman utama
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
